import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spin, Typography, Alert, Button, Row, Col, Space, Radio, Input } from 'antd';
import { useSearchParams } from 'react-router-dom';
import NegativeProposalReasonService, { INegativeProposalReason } from '../../services/NegativeProposalReasonService';
import kontrolxLogo from '../../assets/auth/kontrolxlogo.svg';
import './NegativeReasonSurveyPage.scss';

const { Title, Text } = Typography;
const { TextArea } = Input;

const NegativeReasonSurveyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState<INegativeProposalReason[]>([]);
  const [selectedReasonId, setSelectedReasonId] = useState<number | undefined>(undefined);
  const [reasonText, setReasonText] = useState('');
  const [message, setMessage] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const handleExit = () => {
    window.close();
    setTimeout(() => {
      if (!window.closed) {
        window.location.href = 'about:blank';
      }
    }, 120);
  };

  const proposalId = useMemo(() => {
    const proposalId = Number(searchParams.get('proposalId'));
    if (!Number.isFinite(proposalId) || proposalId <= 0) {
      return undefined;
    }
    return proposalId;
  }, [searchParams]);

  const preselectedReasonId = useMemo(() => {
    const reasonId = Number(searchParams.get('reasonId'));
    if (!Number.isFinite(reasonId) || reasonId <= 0) {
      return undefined;
    }
    return reasonId;
  }, [searchParams]);

  const orderedReasons = useMemo(
    () =>
      [...reasons].sort((a, b) => {
        if (Boolean(a.isOther) !== Boolean(b.isOther)) {
          return Number(Boolean(a.isOther)) - Number(Boolean(b.isOther));
        }
        return (a.text || '').localeCompare(b.text || '', 'tr-TR');
      }),
    [reasons],
  );

  const selectedReason = useMemo(
    () => orderedReasons.find((reason) => reason.id === selectedReasonId),
    [orderedReasons, selectedReasonId],
  );

  useEffect(() => {
    if (!proposalId) {
      setSuccess(false);
      setMessage('Geçersiz anket bağlantısı.');
      setLoading(false);
      return;
    }

    setLoading(true);
    NegativeProposalReasonService.listPublicSurveyReasons()
      .then((response) => {
        setReasons(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        setSuccess(false);
        setMessage('Anket nedenleri yüklenemedi.');
      })
      .finally(() => setLoading(false));
  }, [proposalId]);

  useEffect(() => {
    if (!preselectedReasonId || orderedReasons.length === 0) {
      return;
    }
    const matched = orderedReasons.find((reason) => reason.id === preselectedReasonId);
    if (matched) {
      setSelectedReasonId(matched.id);
    }
  }, [preselectedReasonId, orderedReasons]);

  const handleSubmit = () => {
    if (!proposalId || !selectedReasonId) {
      setSuccess(false);
      setMessage('Lütfen bir neden seçin.');
      return;
    }

    const selectedIsOther = Boolean(selectedReason?.isOther);
    const normalizedReasonText = reasonText.trim();
    if (selectedIsOther && !normalizedReasonText) {
      setSuccess(false);
      setMessage('Diğer nedeni için açıklama girmeniz zorunludur.');
      return;
    }

    setSubmitting(true);
    NegativeProposalReasonService.submitPublicSurveySelection({
      proposalId,
      reasonId: selectedReasonId,
      reasonText: selectedIsOther ? normalizedReasonText : undefined,
    })
      .then((response) => {
        setSuccess(Boolean(response.data.applied));
        setMessage(response.data.message || 'İşlem tamamlandı.');
        setSubmitted(Boolean(response.data.applied));
      })
      .catch((error) => {
        const apiMessage = error?.response?.data?.message || 'Geri bildirim kaydı sırasında hata oluştu.';
        setSuccess(false);
        setMessage(apiMessage);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="negative-reason-survey-page">
      <Row justify="center" align="middle" className="negative-reason-survey-page__row">
        <Col xs={23} sm={20} md={16} lg={13} xl={10}>
            <div className="negative-reason-survey-page__logo-wrap">
              <img
                src={kontrolxLogo}
                alt="KONTROLX Logo"
                className="negative-reason-survey-page__logo"
              />
            </div>
          <Card className="negative-reason-survey-page__card">
        
            <Title level={3} className="negative-reason-survey-page__title">
              KontrolX Geri Bildirim
            </Title>
            {loading ? (
              <Space className="negative-reason-survey-page__loading" size={12}>
                <Spin />
                <Text>Anket hazırlanıyor...</Text>
              </Space>
            ) : (
              <>
                {message && (
                  <Alert
                    className="negative-reason-survey-page__alert"
                    type={success ? 'success' : 'warning'}
                    showIcon
                    message={success ? 'Teşekkür ederiz' : 'Bilgilendirme'}
                    description={message}
                  />
                )}

                {!submitted && proposalId && (
                  <div className="negative-reason-survey-page__form">
                    <Text strong>Olumsuz teklif nedeninizi seçiniz:</Text>
                    <Radio.Group
                      className="negative-reason-survey-page__radio-group"
                      onChange={(event) => {
                        setSelectedReasonId(event.target.value);
                        setMessage('');
                        setSuccess(false);
                      }}
                      value={selectedReasonId}
                    >
                      <Space direction="vertical" size={8}>
                        {orderedReasons.map((reason) => (
                          <Radio key={reason.id} value={reason.id}>
                            {reason.text}
                          </Radio>
                        ))}
                      </Space>
                    </Radio.Group>

                    {selectedReason?.isOther && (
                      <div className="negative-reason-survey-page__other-wrap">
                        <Text strong>Diğer nedeni</Text>
                        <TextArea
                          rows={4}
                          value={reasonText}
                          onChange={(event) => setReasonText(event.target.value)}
                          placeholder="Lütfen olumsuzluk nedeninizi yazın"
                          maxLength={500}
                        />
                      </div>
                    )}

                    <Button type="primary" onClick={handleSubmit} loading={submitting}>
                      Gönder
                    </Button>
                  </div>
                )}
              </>
            )}
            <div className="negative-reason-survey-page__actions">
              <Button onClick={handleExit}>Çıkış</Button>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default NegativeReasonSurveyPage;
